import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Alert, Stack, Typography, Paper } from '@mui/material';
import { buildEventsApiUrl } from '../config/api';

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = searchParams.get('code');

    const verify = async () => {
      if (!code) {
        setError('Verification code is missing.');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(buildEventsApiUrl(`/api/auth/verify-email?code=${encodeURIComponent(code)}`));
        const text = await response.text();

        if (!response.ok) {
          throw new Error(text || 'Email verification failed.');
        }

        setMessage(text || 'Email verified. You can now log in.');
      } catch (err: any) {
        setError(err.message || 'Email verification failed.');
      } finally {
        setLoading(false);
      }
    };

    verify();
  }, [searchParams]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        p: 2
      }}
    >
      <Paper elevation={6} sx={{ width: '100%', maxWidth: 520, p: 4, borderRadius: 3 }}>
        <Stack spacing={2}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Verify Email
          </Typography>

          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={24} />
              <Typography>Verifying your email…</Typography>
            </Box>
          )}

          {!loading && message && <Alert severity="success">{message}</Alert>}
          {!loading && error && <Alert severity="error">{error}</Alert>}

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="contained" onClick={() => navigate('/login')}>
              Go to Login
            </Button>
            <Button variant="text" onClick={() => navigate('/')}>
              Back to Home
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}
